import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/Surface";
import { Button } from "../components/ui/Button";
import { inputClass } from "../components/FormKit";
import { labelize } from "../lib/format";
import {
  getPipeline, moveApplication, addApplicationNote, listApplicationNotes,
  PIPELINE_STAGES, type PipelineApplication, type ApplicationNote,
} from "../lib/pipeline";

const NEXT: Record<string, string[]> = {
  applied: ["reviewing", "rejected"],
  reviewing: ["accepted", "rejected"],
  accepted: [],
  rejected: ["reviewing"],
};

export function Pipeline() {
  const [apps, setApps] = useState<PipelineApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [openNotes, setOpenNotes] = useState<string | null>(null);
  const [notes, setNotes] = useState<ApplicationNote[]>([]);
  const [noteText, setNoteText] = useState("");

  async function refresh() {
    setApps((await getPipeline()).applications);
  }
  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, []);

  async function move(a: PipelineApplication, status: string) {
    setBusy(a.id + status);
    try {
      await moveApplication(a.id, status);
      await refresh();
    } finally {
      setBusy(null);
    }
  }

  async function toggleNotes(id: string) {
    if (openNotes === id) { setOpenNotes(null); return; }
    setOpenNotes(id);
    setNotes((await listApplicationNotes(id)).notes);
  }

  async function submitNote(e: FormEvent, id: string) {
    e.preventDefault();
    if (!noteText.trim()) return;
    setBusy(id + "note");
    try {
      await addApplicationNote(id, noteText);
      setNoteText("");
      setNotes((await listApplicationNotes(id)).notes);
      await refresh();
    } finally {
      setBusy(null);
    }
  }

  const byStage = (stage: string) => apps.filter((a) => a.status === stage);

  return (
    <div className="mx-auto grid w-full max-w-6xl gap-6">
      <PageHeader title="Talent pipeline" subtitle="Every candidate across your job postings, by stage." />
      {loading ? (
        <p className="font-mono text-sm text-muted">Loading…</p>
      ) : apps.length === 0 ? (
        <p className="text-sm text-muted">No applications yet. Post jobs and applicants will appear here as you move them through stages.</p>
      ) : (
        <div className="grid gap-4 lg:grid-cols-4">
          {PIPELINE_STAGES.map((stage) => (
            <div key={stage} className="grid content-start gap-2">
              <h2 className="font-mono text-[11px] font-bold uppercase tracking-widest text-muted">{labelize(stage)} ({byStage(stage).length})</h2>
              {byStage(stage).map((a) => (
                <div key={a.id} className="grid gap-2 rounded-card border border-line bg-surface p-3">
                  <Link to={`/p/${a.applicant_profile_id}`} className="font-bold hover:text-accent-strong">{a.applicant_name ?? "Candidate"}</Link>
                  <p className="font-mono text-[10px] text-muted">{a.job_title}</p>
                  {a.applicant_headline && <p className="line-clamp-2 text-xs text-muted">{a.applicant_headline}</p>}
                  <div className="flex flex-wrap gap-1">
                    {NEXT[a.status]?.map((s) => (
                      <button key={s} type="button" disabled={busy === a.id + s} onClick={() => move(a, s)}
                        className="rounded border border-line px-2 py-0.5 font-mono text-[10px] font-bold uppercase text-muted hover:border-accent hover:text-accent-strong">
                        → {labelize(s)}
                      </button>
                    ))}
                  </div>
                  <button type="button" className="w-max font-mono text-[10px] font-bold text-accent-strong" onClick={() => toggleNotes(a.id)}>
                    {a.note_count} note{a.note_count === 1 ? "" : "s"}
                  </button>
                  {openNotes === a.id && (
                    <div className="grid gap-1 border-t border-line pt-2">
                      {notes.map((n) => <p key={n.id} className="text-xs text-muted">{n.body}</p>)}
                      <form className="flex gap-1" onSubmit={(e) => submitNote(e, a.id)}>
                        <input className={`${inputClass} text-xs`} value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Private note…" />
                        <Button type="submit" disabled={busy === a.id + "note" || !noteText.trim()}>Add</Button>
                      </form>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
